export const SELECTED_VENUE_CONTEXT_STORAGE_KEY = "match.selectedVenueContext";

export type VenueSelectionRecord = {
  venueId: string;
  tenantId: string | null;
};

export type SelectableVenue = {
  id: string;
  tenant_id?: string | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function canUseStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function readSelectedVenueContext(): VenueSelectionRecord | null {
  if (!canUseStorage()) return null;

  try {
    const rawValue = window.localStorage.getItem(SELECTED_VENUE_CONTEXT_STORAGE_KEY);
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue);
    if (!isRecord(parsed) || typeof parsed.venueId !== "string") {
      return null;
    }

    return {
      venueId: parsed.venueId,
      tenantId: typeof parsed.tenantId === "string" ? parsed.tenantId : null,
    };
  } catch {
    return null;
  }
}

export function writeSelectedVenueContext(selection: VenueSelectionRecord | null) {
  if (!canUseStorage()) return;

  if (!selection) {
    window.localStorage.removeItem(SELECTED_VENUE_CONTEXT_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(SELECTED_VENUE_CONTEXT_STORAGE_KEY, JSON.stringify(selection));
}

export function readSelectedVenueTenantId() {
  return readSelectedVenueContext()?.tenantId ?? null;
}

export function resolveSelectedVenueContext<T extends SelectableVenue>(
  venues: T[] | null | undefined,
  currentVenueId: string,
  storedSelection: VenueSelectionRecord | null,
): VenueSelectionRecord | null {
  if (!venues?.length) return null;

  const currentVenue = currentVenueId
    ? venues.find((venue) => venue.id === currentVenueId) ?? null
    : null;
  const storedVenue = storedSelection?.venueId
    ? venues.find((venue) => venue.id === storedSelection.venueId) ?? null
    : null;
  const nextVenue = currentVenue ?? storedVenue ?? venues[0];

  if (!nextVenue) return null;

  return {
    venueId: nextVenue.id,
    tenantId: nextVenue.tenant_id ?? null,
  };
}
